import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from 'utils/apiClient';

interface SessionState {
    expiresAt: number | null; // 절대 만료 시각 (ms timestamp)
    remainingSeconds: number | null;
    maxInactiveInterval: number; // 서버 세션 타임아웃 (초)
    warningSeconds: number; // 경고 표시 기준 (서버에서 환경별로 설정)
    showWarning: boolean;
    showAlert: boolean;
    isExtending: boolean;
    dismissed: boolean;
}

const initialState: SessionState = {
    expiresAt: null,
    remainingSeconds: null,
    maxInactiveInterval: 1800,
    warningSeconds: 300,
    showWarning: false,
    showAlert: false,
    isExtending: false,
    dismissed: false,
};

export const fetchSessionInfo = createAsyncThunk(
    'session/fetchInfo',
    async () => {
        const response = await apiClient.get<{ remainingSeconds: number; maxInactiveInterval: number; warningSeconds: number }>('/api/session/info');
        return response.data;
    }
);

export const extendSession = createAsyncThunk(
    'session/extend',
    async () => {
        const response = await apiClient.post<{ remainingSeconds: number }>('/api/session/extend');
        return response.data;
    }
);

const sessionSlice = createSlice({
    name: 'session',
    initialState,
    reducers: {
        tick(state) {
            if (state.expiresAt === null) return;
            const remaining = Math.floor((state.expiresAt - Date.now()) / 1000);
            state.remainingSeconds = Math.max(0, remaining);

            if (remaining <= state.warningSeconds && !state.showWarning && !state.dismissed) {
                state.showWarning = true;
                state.showAlert = true;
                try {
                    localStorage.setItem('GD_SESSION_WARNING', String(Date.now()));
                } catch (e) {
                    // localStorage 접근 불가 시 무시
                }
            }
        },
        dismissAlert(state) {
            state.showAlert = false;
        },
        dismissWarning(state) {
            state.dismissed = true;
            state.showWarning = false;
            state.showAlert = false;
        },
        refreshTimer(state) {
            if (state.expiresAt === null) return;
            state.expiresAt = Date.now() + state.maxInactiveInterval * 1000;
            state.remainingSeconds = state.maxInactiveInterval;
            if (state.showWarning) {
                state.showWarning = false;
                state.showAlert = false;
                state.dismissed = false;
            }
        },
        resetSession(state) {
            state.expiresAt = null;
            state.remainingSeconds = null;
            state.showWarning = false;
            state.showAlert = false;
            state.isExtending = false;
            state.dismissed = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSessionInfo.fulfilled, (state, action) => {
                const { remainingSeconds, maxInactiveInterval, warningSeconds } = action.payload;
                state.expiresAt = Date.now() + (remainingSeconds * 1000);
                state.remainingSeconds = remainingSeconds;
                state.maxInactiveInterval = maxInactiveInterval;
                state.warningSeconds = warningSeconds;
                if (remainingSeconds <= warningSeconds && !state.showWarning && !state.dismissed) {
                    state.showWarning = true;
                    state.showAlert = true;
                } else if (remainingSeconds > warningSeconds) {
                    state.showWarning = false;
                    state.showAlert = false;
                    state.dismissed = false;
                }
            })
            .addCase(extendSession.pending, (state) => {
                state.isExtending = true;
            })
            .addCase(extendSession.fulfilled, (state, action) => {
                state.expiresAt = Date.now() + (action.payload.remainingSeconds * 1000);
                state.remainingSeconds = action.payload.remainingSeconds;
                state.showWarning = false;
                state.showAlert = false;
                state.isExtending = false;
                state.dismissed = false;
                try {
                    localStorage.setItem('GD_SESSION_EXTENDED', String(Date.now()));
                } catch (e) {
                    // localStorage 접근 불가 시 무시 (private browsing 등)
                }
            })
            .addCase(extendSession.rejected, (state) => {
                state.isExtending = false;
            });
    },
});

export const { tick, dismissAlert, dismissWarning, refreshTimer, resetSession } = sessionSlice.actions;
export default sessionSlice.reducer;
